import {
  EMPTY_MSK_REGION,
  type ExamImage,
  MSK_INDICATIONS,
  MSK_REGIONS,
  type MskBillingLevel,
  type MskFindingValue,
  type MskIndication,
  type MskRegion,
  type MskRegionData,
  type MskWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import type { UsBillingCode } from "./us/us-fields";
import type { WorksheetFieldDef } from "./worksheet-field-defs";

type MskView = { id: string; label: string };

type ShowWhen = (data: MskWorksheetData, region: MskRegion) => boolean;

export type MskFindingPlacement = "header" | "indication" | "impression";

export type MskFindingDef = WorksheetFieldDef<ShowWhen> & {
  placement?: MskFindingPlacement;
};

type MskRegionConfig = {
  label: string;
  views: readonly MskView[];
  findings: readonly MskFindingDef[];
  completeExamFindings?: readonly string[];
  technicallyLimited?: boolean;
  billing: { codes: readonly UsBillingCode[] };
};

export const MSK_DEFINITION_VERSION = 1;

export const mskRegionData = (
  data: MskWorksheetData,
  region: MskRegion,
): MskRegionData => data.regions[region] ?? EMPTY_MSK_REGION;

const PRESENT = { value: "present", label: "Present" };
const ABSENT = { value: "absent", label: "Absent" };
const INDETERMINATE = { value: "indeterminate", label: "Indeterminate" };
const NOT_ASSESSED = { value: "not_assessed", label: "Not Assessed" };
const NORMAL = { value: "normal", label: "Normal" };
const INTACT = { value: "intact", label: "Intact" };
const PARTIAL_TEAR = { value: "partial_tear", label: "Partial Tear" };
const TENDINOPATHY = { value: "tendinopathy", label: "Tendinopathy" };
const SUBLUXATION = { value: "subluxation", label: "Subluxation" };

const PRESENCE = [PRESENT, ABSENT, INDETERMINATE, NOT_ASSESSED];
const PRESENT_ABSENT = [PRESENT, ABSENT, NOT_ASSESSED];

const DOPPLER: MskFindingDef = {
  kind: "single",
  id: "doppler",
  label: "Doppler Signal",
  options: PRESENT_ABSENT,
  normal: NOT_ASSESSED.value,
};

const effusionCharacter = (effusionId: string): MskFindingDef => ({
  kind: "single",
  id: "effusionCharacter",
  label: "Effusion Character",
  options: [
    { value: "simple", label: "Simple" },
    { value: "complex", label: "Complex" },
    NOT_ASSESSED,
  ],
  normal: null,
  showWhen: (data, region) =>
    mskRegionData(data, region).findings[effusionId] === PRESENT.value,
});

export const mskRegionIndications = (region: MskRegion): MskIndication[] =>
  (Object.keys(MSK_INDICATIONS) as MskIndication[]).filter(
    (indication) => MSK_INDICATIONS[indication].region === region,
  );

export const mskViewKey = (region: MskRegion, viewId: string): string =>
  `${region}_${viewId}`;

export const MSK_NORMAL_IMPRESSION = "no_abnormality";

const MSK_IMPRESSION: readonly MskFindingDef[] = [
  {
    kind: "multi",
    id: "msk.impression",
    label: "Impression",
    placement: "impression",
    options: [
      {
        value: MSK_NORMAL_IMPRESSION,
        label: "No sonographic evidence of musculoskeletal abnormality",
      },
      { value: "joint_effusion", label: "Joint effusion" },
      { value: "joint_dislocation", label: "Joint dislocation" },
      { value: "tendon_tear", label: "Tendon tear" },
      { value: "fracture", label: "Fracture" },
      { value: "muscle_tear", label: "Muscle tear" },
      { value: "indeterminate", label: "Indeterminate" },
      { value: "other", label: "Other" },
    ],
  },
  {
    kind: "text",
    id: "msk.impression_other",
    label: "Impression, other",
    placement: "impression",
    showWhen: (data, region) => {
      const impression = mskRegionData(data, region).findings["msk.impression"];
      return Array.isArray(impression) && impression.includes("other");
    },
  },
];

const EXTREMITY_BILLING = {
  codes: [
    {
      cpt: "76882",
      label: "Extremity ultrasound, (non-vascular)",
      preTick: true,
    },
  ],
};

const presence = (
  id: string,
  label: string,
  absent: { value: string; label: string },
  present: { value: string; label: string },
): MskFindingDef => ({
  kind: "single",
  id,
  label,
  options: [absent, present],
  normal: absent.value,
});

const OTHER_LOCATION: MskRegionConfig = {
  label: "Other location",
  views: [],
  technicallyLimited: true,
  findings: [
    {
      kind: "text",
      id: "msk.location",
      label: "Musculoskeletal location being scanned",
      placement: "header",
    },
    {
      kind: "text",
      id: "msk.indication_other",
      label: "Indication, other",
      placement: "indication",
      showWhen: (data, region) =>
        mskRegionData(data, region).indication === "other_other",
    },
    presence(
      "msk.joint_effusion",
      "Joint",
      { value: "no_effusion", label: "No Effusion" },
      { value: "effusion", label: "Effusion" },
    ),
    presence(
      "msk.joint_dislocation",
      "Joint",
      { value: "no_dislocation", label: "No dislocation" },
      { value: "dislocation", label: "Dislocation" },
    ),
    presence(
      "msk.fracture",
      "Bone",
      { value: "absent", label: "Fracture absent" },
      { value: "present", label: "Fracture present" },
    ),
    {
      kind: "single",
      id: "msk.tendon",
      label: "Tendon",
      options: [
        { value: "no_disruption", label: "No tendinous disruption" },
        { value: "partial_tear", label: "Partial tear" },
        { value: "complete_tear", label: "Complete tear" },
      ],
      normal: "no_disruption",
    },
    presence(
      "msk.muscle_tear",
      "Muscle",
      { value: "absent", label: "Tear absent" },
      { value: "present", label: "Tear present" },
    ),
    { kind: "text", id: "msk.other_findings", label: "Other" },
    ...MSK_IMPRESSION,
  ],
  billing: {
    codes: [
      { cpt: "76536", label: "Head and neck ultrasound" },
      { cpt: "76882", label: "Extremity ultrasound, (non-vascular)" },
      { cpt: "76604", label: "Chest wall ultrasound" },
      { cpt: "76705", label: "Abdominal wall or lower back ultrasound" },
      { cpt: "76857", label: "Pelvic Wall (non-obstetric) ultrasound" },
    ],
  },
};

const withImpressionAndBilling = (
  config: Omit<MskRegionConfig, "billing">,
): MskRegionConfig => ({
  ...config,
  findings: [...config.findings, ...MSK_IMPRESSION],
  billing: EXTREMITY_BILLING,
});

export const MSK_REGION_CONFIG: Record<MskRegion, MskRegionConfig> = {
  shoulder: withImpressionAndBilling({
    label: "Shoulder",
    views: [
      {
        id: "posterior",
        label: "Posterior (Infraspinatus / Glenohumeral Joint)",
      },
      { id: "bicepsTransverse", label: "Long Head Biceps Tendon – Transverse" },
      {
        id: "bicepsLongitudinal",
        label: "Long Head Biceps Tendon – Longitudinal",
      },
      { id: "subscapularis", label: "Subscapularis" },
      { id: "supraspinatus", label: "Supraspinatus (Crass Position)" },
      { id: "subacromialBursa", label: "Subacromial-Subdeltoid Bursa" },
      { id: "dynamic", label: "Dynamic (Abduction / External Rotation)" },
    ],
    findings: [
      {
        kind: "single",
        id: "glenohumeralEffusion",
        label: "Glenohumeral Effusion",
        options: PRESENCE,
        normal: ABSENT.value,
      },
      effusionCharacter("glenohumeralEffusion"),
      {
        kind: "single",
        id: "rotatorCuffTear",
        label: "Rotator Cuff Tear",
        options: PRESENCE,
        normal: ABSENT.value,
      },
      {
        kind: "single",
        id: "bicepsTendon",
        label: "Biceps Tendon",
        options: [
          NORMAL,
          TENDINOPATHY,
          SUBLUXATION,
          { value: "rupture", label: "Rupture" },
          NOT_ASSESSED,
        ],
        normal: NORMAL.value,
      },
      {
        kind: "single",
        id: "bursalFluid",
        label: "Subacromial-Subdeltoid Bursal Fluid",
        options: PRESENT_ABSENT,
        normal: ABSENT.value,
      },
      {
        kind: "single",
        id: "calcificDeposit",
        label: "Calcific Deposit",
        options: PRESENT_ABSENT,
        normal: ABSENT.value,
      },
      DOPPLER,
    ],
    completeExamFindings: [
      "glenohumeralEffusion",
      "effusionCharacter",
      "rotatorCuffTear",
      "bicepsTendon",
      "bursalFluid",
      "calcificDeposit",
    ],
  }),
  knee: withImpressionAndBilling({
    label: "Knee",
    views: [
      { id: "suprapatellarLongitudinal", label: "Suprapatellar Longitudinal" },
      { id: "suprapatellarTransverse", label: "Suprapatellar Transverse" },
      { id: "medialRecess", label: "Medial Recess" },
      { id: "lateralRecess", label: "Lateral Recess" },
      { id: "poplitealFossa", label: "Popliteal Fossa (Posterior)" },
      { id: "quadricepsTendon", label: "Quadriceps Tendon" },
      { id: "patellarTendon", label: "Patellar Tendon" },
      { id: "dynamic", label: "Dynamic (Flexion / Extension)" },
    ],
    findings: [
      {
        kind: "single",
        id: "jointEffusion",
        label: "Joint Effusion",
        options: PRESENCE,
        normal: ABSENT.value,
      },
      effusionCharacter("jointEffusion"),
      {
        kind: "single",
        id: "synovialThickening",
        label: "Synovial Thickening",
        options: PRESENT_ABSENT,
        normal: ABSENT.value,
      },
      {
        kind: "single",
        id: "bakersCyst",
        label: "Baker's Cyst",
        options: [
          PRESENT,
          ABSENT,
          { value: "ruptured", label: "Ruptured" },
          NOT_ASSESSED,
        ],
        normal: ABSENT.value,
      },
      {
        kind: "single",
        id: "extensorTendon",
        label: "Quadriceps / Patellar Tendon",
        options: [
          NORMAL,
          PARTIAL_TEAR,
          { value: "full_tear", label: "Full Tear" },
          NOT_ASSESSED,
        ],
        normal: NORMAL.value,
      },
      DOPPLER,
    ],
    completeExamFindings: [
      "jointEffusion",
      "effusionCharacter",
      "synovialThickening",
      "bakersCyst",
      "extensorTendon",
    ],
  }),
  ankle: withImpressionAndBilling({
    label: "Ankle",
    views: [
      {
        id: "anteriorLongitudinal",
        label: "Anterior Longitudinal (Tibiotalar Joint)",
      },
      { id: "anteriorTransverse", label: "Anterior Transverse" },
      { id: "atfl", label: "Anterior Talofibular Ligament" },
      { id: "peronealTendons", label: "Peroneal Tendons" },
      { id: "tibialisPosterior", label: "Tibialis Posterior Tendon" },
      { id: "dynamic", label: "Dynamic (Anterior Drawer / Stress)" },
    ],
    findings: [
      {
        kind: "single",
        id: "jointEffusion",
        label: "Joint Effusion",
        options: PRESENCE,
        normal: ABSENT.value,
      },
      {
        kind: "single",
        id: "atflIntegrity",
        label: "ATFL Integrity",
        options: [
          INTACT,
          PARTIAL_TEAR,
          { value: "full_tear", label: "Full Tear" },
          NOT_ASSESSED,
        ],
        normal: INTACT.value,
      },
      {
        kind: "single",
        id: "tendonAppearance",
        label: "Tendon Appearance",
        options: [
          NORMAL,
          TENDINOPATHY,
          { value: "tear", label: "Tear" },
          SUBLUXATION,
          NOT_ASSESSED,
        ],
        normal: NORMAL.value,
      },
      {
        kind: "single",
        id: "tenosynovitis",
        label: "Tenosynovitis",
        options: PRESENT_ABSENT,
        normal: ABSENT.value,
      },
      DOPPLER,
    ],
    completeExamFindings: [
      "jointEffusion",
      "atflIntegrity",
      "tendonAppearance",
      "tenosynovitis",
    ],
  }),
  achilles: withImpressionAndBilling({
    label: "Achilles",
    views: [
      { id: "longitudinal", label: "Longitudinal" },
      { id: "transverse", label: "Transverse" },
      { id: "calcanealInsertion", label: "Calcaneal Insertion" },
      { id: "dynamic", label: "Dynamic (Plantar / Dorsiflexion)" },
    ],
    findings: [
      {
        kind: "single",
        id: "tendonContinuity",
        label: "Tendon Continuity",
        options: [
          INTACT,
          PARTIAL_TEAR,
          { value: "complete_rupture", label: "Complete Rupture" },
          NOT_ASSESSED,
        ],
        normal: INTACT.value,
      },
      {
        kind: "number",
        id: "tendonThicknessMm",
        label: "Tendon Thickness",
        unit: "mm",
      },
      {
        kind: "single",
        id: "tendonEchotexture",
        label: "Tendon Echotexture",
        options: [
          NORMAL,
          { value: "heterogeneous", label: "Heterogeneous" },
          NOT_ASSESSED,
        ],
        normal: NORMAL.value,
      },
      {
        kind: "single",
        id: "paratenonFluid",
        label: "Paratenon Fluid",
        options: PRESENT_ABSENT,
        normal: ABSENT.value,
      },
      {
        kind: "number",
        id: "gapSizeMm",
        label: "Gap Size on Dynamic Testing",
        unit: "mm",
        showWhen: (data, region) =>
          data.views[mskViewKey(region, "dynamic")] === true,
      },
      DOPPLER,
    ],
  }),
  wristHand: withImpressionAndBilling({
    label: "Wrist / Hand",
    views: [
      {
        id: "extensorLongitudinal",
        label: "Extensor Compartments – Longitudinal",
      },
      { id: "extensorTransverse", label: "Extensor Compartments – Transverse" },
      { id: "flexorLongitudinal", label: "Flexor Tendons – Longitudinal" },
      { id: "flexorTransverse", label: "Flexor Tendons – Transverse" },
      { id: "dynamic", label: "Dynamic (Finger / Wrist Movement)" },
    ],
    findings: [
      {
        kind: "multi",
        id: "compartments",
        label: "Compartment / Tendon Assessed",
        options: [
          ...[1, 2, 3, 4, 5, 6].map((n) => ({
            value: `extensor_${n}`,
            label: `Extensor Compartment ${n}`,
          })),
          { value: "flexor", label: "Flexor Tendon" },
        ],
      },
      {
        kind: "single",
        id: "tenosynovitis",
        label: "Tenosynovitis",
        options: PRESENT_ABSENT,
        normal: ABSENT.value,
      },
      {
        kind: "single",
        id: "tendonIntegrity",
        label: "Tendon Integrity",
        options: [
          INTACT,
          PARTIAL_TEAR,
          { value: "complete_rupture", label: "Complete Rupture" },
          NOT_ASSESSED,
        ],
        normal: INTACT.value,
      },
      {
        kind: "single",
        id: "fluidCollection",
        label: "Fluid Collection / Ganglion",
        options: PRESENT_ABSENT,
        normal: ABSENT.value,
      },
      {
        kind: "single",
        id: "foreignBody",
        label: "Foreign Body",
        options: PRESENT_ABSENT,
        normal: ABSENT.value,
      },
      DOPPLER,
    ],
  }),
  other: OTHER_LOCATION,
};

export const isMskFindingShown = (
  data: MskWorksheetData,
  region: MskRegion,
  finding: MskFindingDef,
): boolean =>
  finding.kind === "multi" ||
  !finding.showWhen ||
  finding.showWhen(data, region);

const isAnswered = (value: MskFindingValue | undefined): boolean =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined && value !== "";

const hasAnyView = (data: MskWorksheetData, region: MskRegion): boolean =>
  MSK_REGION_CONFIG[region].views.some(
    (view) => data.views[mskViewKey(region, view.id)] === true,
  );

export const isMskRegionScanned = (
  data: MskWorksheetData,
  region: MskRegion,
): boolean => {
  const regionData = mskRegionData(data, region);
  return (
    hasAnyView(data, region) ||
    regionData.side !== null ||
    regionData.indication !== null ||
    Object.values(regionData.findings).some(isAnswered)
  );
};

export const isMskRegionComplete = (
  data: MskWorksheetData,
  region: MskRegion,
): boolean => {
  const config = MSK_REGION_CONFIG[region];
  const regionData = mskRegionData(data, region);
  return (
    regionData.side !== null &&
    regionData.indication !== null &&
    (config.views.length === 0 || hasAnyView(data, region)) &&
    config.findings
      .filter(
        (finding) =>
          finding.kind !== "number" &&
          finding.kind !== "text" &&
          !(regionData.technicallyLimited && !finding.placement) &&
          isMskFindingShown(data, region, finding),
      )
      .every((finding) => isAnswered(regionData.findings[finding.id]))
  );
};

export const scannedMskRegions = (data: MskWorksheetData): MskRegion[] =>
  MSK_REGIONS.filter((region) => isMskRegionScanned(data, region));

export const isMskComplete = (data: MskWorksheetData): boolean => {
  const scanned = scannedMskRegions(data);
  return (
    scanned.length > 0 &&
    scanned.every((region) => isMskRegionComplete(data, region))
  );
};

export const normalizeMsk = (data: MskWorksheetData): MskWorksheetData => {
  const regions = Object.fromEntries(
    MSK_REGIONS.map((region) => {
      const regionData = mskRegionData(data, region);
      const findings = Object.fromEntries(
        MSK_REGION_CONFIG[region].findings
          .filter(
            (finding) =>
              finding.id in regionData.findings &&
              isMskFindingShown(data, region, finding),
          )
          .map((finding) => [finding.id, regionData.findings[finding.id]]),
      );
      return [region, { ...regionData, findings }];
    }),
  ) as Record<MskRegion, MskRegionData>;

  const normalized = { ...data, regions };
  const scannedIndications = scannedMskRegions(normalized)
    .map((region) => mskRegionData(normalized, region).indication)
    .filter((indication) => indication !== null);
  const indication =
    data.indication !== null && scannedIndications.includes(data.indication)
      ? data.indication
      : (scannedIndications[0] ?? null);

  return {
    ...normalized,
    definitionVersion: MSK_DEFINITION_VERSION,
    indication,
  };
};

export const markMskRegionNormal = (
  data: MskWorksheetData,
  region: MskRegion,
): MskWorksheetData => {
  const regionData = mskRegionData(data, region);
  const normals: Record<string, MskFindingValue> = {
    ...Object.fromEntries(
      MSK_REGION_CONFIG[region].findings.flatMap((finding) =>
        finding.kind === "single" && finding.normal !== null
          ? [[finding.id, finding.normal]]
          : [],
      ),
    ),
    "msk.impression": [MSK_NORMAL_IMPRESSION],
  };

  return normalizeMsk({
    ...data,
    regions: {
      ...data.regions,
      [region]: {
        ...regionData,
        findings: { ...regionData.findings, ...normals },
      },
    },
  });
};

export const untaggedMskImages = <T extends ExamImage>(
  images: readonly T[],
): T[] => images.filter((image) => !image.mskRegion);

const UNREMARKABLE_VALUES: ReadonlySet<string> = new Set([
  "absent",
  "not_assessed",
]);

const optionLabel = (finding: MskFindingDef, value: string): string =>
  finding.kind === "number" || finding.kind === "text"
    ? value
    : (finding.options.find((option) => option.value === value)?.label ??
      value);

export const describeMskRegionFindings = (
  data: MskWorksheetData,
  region: MskRegion,
): string[] => {
  const { findings } = mskRegionData(data, region);
  return MSK_REGION_CONFIG[region].findings.flatMap((finding) => {
    const value = findings[finding.id];
    if (finding.kind === "text") {
      return typeof value === "string" && value
        ? [`${finding.label}: ${value}`]
        : [];
    }
    if (finding.kind === "multi") {
      const listed = Array.isArray(value)
        ? value.filter((v) => v !== MSK_NORMAL_IMPRESSION)
        : [];
      return listed.length > 0
        ? [
            `${finding.label}: ${listed.map((v) => optionLabel(finding, v)).join(", ")}`,
          ]
        : [];
    }
    if (finding.kind === "number") {
      return typeof value === "number"
        ? [`${finding.label} ${value} ${finding.unit}`]
        : [];
    }
    if (
      typeof value !== "string" ||
      value === finding.normal ||
      UNREMARKABLE_VALUES.has(value)
    ) {
      return [];
    }
    return [`${finding.label}: ${optionLabel(finding, value).toLowerCase()}`];
  });
};

export const mskImageGroupLabel = (image: ExamImage): string =>
  image.mskRegion ? MSK_REGION_CONFIG[image.mskRegion].label : "Untagged";

export const sortImagesByMskRegion = <T extends ExamImage>(
  images: readonly T[],
): T[] => {
  const rank = (image: T) =>
    image.mskRegion ? MSK_REGIONS.indexOf(image.mskRegion) : MSK_REGIONS.length;
  return [...images].sort((a, b) => rank(a) - rank(b));
};

export const supportsCompleteMskExam = (
  data: MskWorksheetData,
  region: MskRegion,
): boolean => {
  const structures = MSK_REGION_CONFIG[region].completeExamFindings;
  if (!structures) return false;
  const { findings } = mskRegionData(data, region);
  return MSK_REGION_CONFIG[region].findings
    .filter(
      (finding) =>
        structures.includes(finding.id) &&
        isMskFindingShown(data, region, finding),
    )
    .every((finding) => {
      const value = findings[finding.id];
      return typeof value === "string" && value !== "not_assessed";
    });
};

export const mskBillingLevel = (
  data: MskWorksheetData,
  region: MskRegion,
): MskBillingLevel =>
  mskRegionData(data, region).billingLevel === "complete" &&
  supportsCompleteMskExam(data, region)
    ? "complete"
    : "limited";
